import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Lightbulb,
  TrendingUp,
  Calendar,
  DollarSign,
  Users,
  FileText,
} from "lucide-react";
import { toast } from "sonner";

export default function RndProjects() {
  return (
    <DashboardLayout>
      <RndProjectsContent />
    </DashboardLayout>
  );
}

function RndProjectsContent() {
  const { selectedCompanyId } = useCompany();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [includeCompleted, setIncludeCompleted] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    startDate: new Date().toISOString().split("T")[0],
    endDate: "",
    status: "planning" as "planning" | "active" | "completed" | "cancelled",
    budgetPlanned: "",
    researchArea: "",
    teamSize: "",
    eligibleForPatentBox: false,
    patentNumber: "",
    notes: "",
  });

  const {
    data: projects,
    isLoading,
    refetch,
  } = trpc.rndProjects.list.useQuery(
    { companyId: selectedCompanyId || 0, includeCompleted },
    { enabled: !!selectedCompanyId }
  );

  const { data: stats } = trpc.rndProjects.getStatistics.useQuery(
    { companyId: selectedCompanyId || 0 },
    { enabled: !!selectedCompanyId }
  );

  const createMutation = trpc.rndProjects.create.useMutation({
    onSuccess: () => {
      toast.success("R&D project created successfully");
      setCreateDialogOpen(false);
      refetch();
      resetForm();
    },
    onError: error => {
      toast.error("Failed to create project", { description: error.message });
    },
  });

  const deleteMutation = trpc.rndProjects.delete.useMutation({
    onSuccess: () => {
      toast.success("R&D project deleted");
      refetch();
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      startDate: new Date().toISOString().split("T")[0],
      endDate: "",
      status: "planning",
      budgetPlanned: "",
      researchArea: "",
      teamSize: "",
      eligibleForPatentBox: false,
      patentNumber: "",
      notes: "",
    });
  };

  const handleCreate = () => {
    if (!selectedCompanyId) return;

    createMutation.mutate({
      companyId: selectedCompanyId,
      name: formData.name,
      description: formData.description || undefined,
      startDate: new Date(formData.startDate).getTime(),
      endDate: formData.endDate
        ? new Date(formData.endDate).getTime()
        : undefined,
      status: formData.status,
      budgetPlanned: formData.budgetPlanned
        ? Math.floor(parseFloat(formData.budgetPlanned) * 100)
        : undefined,
      researchArea: formData.researchArea || undefined,
      teamSize: formData.teamSize ? parseInt(formData.teamSize) : undefined,
      eligibleForPatentBox: formData.eligibleForPatentBox,
      patentNumber: formData.patentNumber || undefined,
      notes: formData.notes || undefined,
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<
      string,
      "default" | "secondary" | "destructive" | "outline"
    > = {
      planning: "outline",
      active: "default",
      completed: "secondary",
      cancelled: "destructive",
    };
    return (
      <Badge variant={variants[status] || "outline"}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  if (!selectedCompanyId) {
    return (
      <div className="p-8">
        <p className="text-muted-foreground">
          Please select a company to view R&D projects.
        </p>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">R&D Projects</h1>
          <p className="text-muted-foreground">
            Manage research & development projects for tax deduction claims
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New R&D Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create R&D Project</DialogTitle>
              <DialogDescription>
                Track research and development projects for Swiss Patent Box and
                R&D tax deductions
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Project Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={e =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., AI-Powered Analytics Engine"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={e =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Brief description of the R&D project..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startDate">Start Date *</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={e =>
                      setFormData({ ...formData, startDate: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={formData.endDate}
                    onChange={e =>
                      setFormData({ ...formData, endDate: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value: any) =>
                      setFormData({ ...formData, status: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planning">Planning</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="budgetPlanned">Planned Budget (CHF)</Label>
                  <Input
                    id="budgetPlanned"
                    type="number"
                    value={formData.budgetPlanned}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        budgetPlanned: e.target.value,
                      })
                    }
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="researchArea">Research Area</Label>
                  <Input
                    id="researchArea"
                    value={formData.researchArea}
                    onChange={e =>
                      setFormData({ ...formData, researchArea: e.target.value })
                    }
                    placeholder="e.g., Machine Learning, Biotechnology"
                  />
                </div>
                <div>
                  <Label htmlFor="teamSize">Team Size</Label>
                  <Input
                    id="teamSize"
                    type="number"
                    value={formData.teamSize}
                    onChange={e =>
                      setFormData({ ...formData, teamSize: e.target.value })
                    }
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="eligibleForPatentBox"
                  checked={formData.eligibleForPatentBox}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      eligibleForPatentBox: e.target.checked,
                    })
                  }
                  className="rounded border-input"
                />
                <Label htmlFor="eligibleForPatentBox" className="font-normal">
                  Eligible for Swiss Patent Box deduction
                </Label>
              </div>

              {formData.eligibleForPatentBox && (
                <div>
                  <Label htmlFor="patentNumber">Patent Number</Label>
                  <Input
                    id="patentNumber"
                    value={formData.patentNumber}
                    onChange={e =>
                      setFormData({ ...formData, patentNumber: e.target.value })
                    }
                    placeholder="CH123456"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={e =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setCreateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={!formData.name || createMutation.isPending}
                >
                  {createMutation.isPending ? "Creating..." : "Create Project"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Projects
              </CardTitle>
              <Lightbulb className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalProjects}</div>
              <p className="text-xs text-muted-foreground">
                {stats.activeProjects} active
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Patent Box Eligible
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.patentBoxEligible}
              </div>
              <p className="text-xs text-muted-foreground">projects with IP</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Planned Budget
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                CHF {(stats.totalBudgetPlanned ?? 0).toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                {(stats.budgetUtilization ?? 0).toFixed(1)}% utilized
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.completedProjects}
              </div>
              <p className="text-xs text-muted-foreground">finished projects</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Projects List */}
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="includeCompleted"
          checked={includeCompleted}
          onChange={e => setIncludeCompleted(e.target.checked)}
          className="rounded border-input"
        />
        <Label htmlFor="includeCompleted" className="font-normal">
          Show completed projects
        </Label>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading R&D projects...</p>
        </div>
      ) : projects && projects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projects.map(project => (
            <Card key={project.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {project.name}
                      {project.eligibleForPatentBox && (
                        <Badge variant="outline" className="text-xs">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          Patent Box
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription>{project.description}</CardDescription>
                  </div>
                  {getStatusBadge(project.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {project.researchArea && (
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Lightbulb className="h-4 w-4 mr-2" />
                    {project.researchArea}
                  </div>
                )}

                <div className="flex items-center text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4 mr-2" />
                  {new Date(project.startDate).toLocaleDateString()}
                  {project.endDate &&
                    ` - ${new Date(project.endDate).toLocaleDateString()}`}
                </div>

                {project.budgetPlanned && (
                  <div className="flex items-center text-sm text-muted-foreground">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Budget: CHF {(project.budgetPlanned / 100).toLocaleString()}
                  </div>
                )}

                {project.teamSize && (
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Users className="h-4 w-4 mr-2" />
                    Team: {project.teamSize} members
                  </div>
                )}

                {project.patentNumber && (
                  <div className="text-sm text-muted-foreground">
                    Patent: {project.patentNumber}
                  </div>
                )}

                <div className="flex justify-end space-x-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      deleteMutation.mutate({
                        id: project.id,
                        companyId: selectedCompanyId,
                      })
                    }
                    disabled={deleteMutation.isPending}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Lightbulb className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No R&D Projects Yet</h3>
            <p className="text-muted-foreground mb-4">
              Start tracking your research and development projects to claim tax
              deductions
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Project
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
